import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { FileSpreadsheet, Upload, AlertTriangle, Loader, Users } from 'lucide-react';
import { Department } from '../../types';
import { ProcessedCustomer } from '../../services/ai/excelProcessor';
import { useStore } from '../../store/useStore';
import { format } from 'date-fns';
import { processExcelData } from '../../services/ai/excelProcessor';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

interface ExcelTaskProcessorProps {
  department: Department;
}

export const ExcelTaskProcessor: React.FC<ExcelTaskProcessorProps> = ({ 
  department
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedData, setProcessedData] = useState<ProcessedCustomer[]>([]);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('17:00');
  const { currentUser, addTask, availableUsers } = useStore();

  // Get department employees for assignment
  const employees = availableUsers.filter(user => user.department === department);

  // Only show for A/R and A/P departments
  if (department !== 'ACCOUNTS_RECEIVABLE' && department !== 'ACCOUNTS_PAYABLE') {
    return null;
  }

  const processExcelFile = async (file: File) => {
    try {
      setIsProcessing(true);
      
      // Read file content
      const reader = new FileReader();
      reader.onload = async (e) => {
        const content = e.target?.result as string;
        
        try {
          // Process with OpenAI
          const customers = await processExcelData(content, department);
          setProcessedData(customers);
          toast.success(`Found ${customers.length} customers to process`);
        } catch (error) {
          console.error('AI processing error:', error);
          toast.error('Failed to process Excel data');
        }
      };

      reader.readAsText(file);
    } catch (error) {
      console.error('Excel processing error:', error);
      toast.error('Failed to process Excel file');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateTasks = () => {
    if (!selectedAssignee) {
      toast.error('Please select an assignee');
      return;
    }

    if (!dueDate) {
      toast.error('Please set a due date');
      return;
    }

    // Create tasks for each customer
    for (const customer of processedData) {
      const task = {
        id: `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        title: `Process ${customer.customerName} Account`,
        description: `Over 45 Days Amount: $${customer.over45Days.toFixed(2)}`,
        assignedTo: selectedAssignee,
        assignedBy: currentUser?.id || '',
        department,
        priority: customer.over45Days > 0 ? 'URGENT' : 'MEDIUM',
        dueDate: new Date(`${dueDate}T${dueTime}`),
        status: 'PENDING',
        createdAt: new Date(),
        updatedAt: new Date(),
        acknowledged: false,
        comments: [],
        subtasks: customer.subtasks.map((st, idx) => ({
          id: `subtask_${Date.now()}_${idx}`,
          title: st.title,
          description: st.description || '',
          dueDate: new Date(`${dueDate}T${dueTime}`),
          status: 'PENDING',
          acknowledged: false,
          hasDueDate: true
        }))
      };

      addTask(task);
    }

    toast.success(`Created ${processedData.length} tasks with ${
      processedData.reduce((sum, c) => sum + c.subtasks.length, 0)
    } total subtasks`);

    // Reset form
    setProcessedData([]);
    setSelectedAssignee('');
    setDueDate('');
  };

  return (
    <Card className="p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <FileSpreadsheet className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Excel Data Import</h2>
            <p className="text-sm text-gray-600">
              Import {department === 'ACCOUNTS_RECEIVABLE' ? 'A/R' : 'A/P'} data from Excel
            </p>
          </div>
        </div>
        {isProcessing && <Loader className="w-5 h-5 text-blue-600 animate-spin" />}
      </div>

      <div className="space-y-4">
        <AnimatePresence>
          {!processedData.length ? (
            <motion.div 
              className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center"
              whileHover={{ borderColor: '#60A5FA' }}
              whileTap={{ scale: 0.98 }}
            >
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    processExcelFile(file);
                  }
                }}
                className="hidden"
                id="excel-upload"
                disabled={isProcessing}
              />
              <label
                htmlFor="excel-upload"
                className={`cursor-pointer flex flex-col items-center gap-2 ${isProcessing ? 'opacity-50' : ''}`}
              >
                <Upload className="w-8 h-8 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">
                  {isProcessing ? 'Processing...' : 'Click to upload Excel file'}
                </span>
                <span className="text-xs text-gray-500">
                  Supports Excel and CSV formats
                </span>
              </label>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              <div className="p-4 bg-emerald-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-emerald-900">Ready to Create Tasks</h3>
                    <p className="text-sm text-emerald-700">
                      {processedData.length} customers with {
                        processedData.reduce((sum, c) => sum + c.subtasks.length, 0)
                      } total items
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setProcessedData([])}
                  >
                    Cancel
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Users className="w-4 h-4 inline-block mr-1" />
                    Assign To
                  </label>
                  <select
                    value={selectedAssignee}
                    onChange={(e) => setSelectedAssignee(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select assignee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
              </div>

              {/* Customer Preview */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Task Preview</h3>
                {processedData.map((customer, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="font-medium text-gray-900">{customer.customerName}</h4>
                        <p className="text-sm text-gray-600">
                          Over 45 Days: ${customer.over45Days.toFixed(2)}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        customer.over45Days > 0 
                          ? 'bg-red-100 text-red-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {customer.over45Days > 0 ? 'URGENT' : 'MEDIUM'}
                      </span>
                    </div>

                    {/* Subtasks Preview */}
                    <div className="mt-4 space-y-2">
                      <h5 className="text-sm font-medium text-gray-700">Subtasks:</h5>
                      {customer.subtasks.map((subtask, idx) => (
                        <div key={idx} className="p-2 bg-gray-50 rounded-lg">
                          <p className="font-medium text-gray-800">{subtask.title}</p>
                          {subtask.description && (
                            <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">
                              {subtask.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <Button
                variant="primary"
                onClick={handleCreateTasks}
                className="w-full"
                disabled={!selectedAssignee || !dueDate}
              >
                Create Tasks
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="bg-amber-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm font-medium">Required Columns</span>
          </div>
          <ul className="mt-2 text-sm text-amber-600 space-y-1 ml-6 list-disc">
            <li>Customer Name</li>
            <li>Date</li>
            <li>Due Date</li>
            <li>Over 45 Days Amount</li>
          </ul>
        </div>
      </div>
    </Card>
  );
};